import { useState } from "react";
import { useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import styled from "styled-components";
import { SectionTitle } from "../../components/SectionTitle";
import { LoadingContainer } from "../../components/LoadingContainer";
import { InfoBox } from "../../components/InfoBox";
import { Button } from "../../components/Button";
import { StyledInput } from "../../components/Input";
import { ConfirmDialog } from "../../components/ConfirmDialog";
import { OneTimeSecret } from "../../components/OneTimeSecret";
import colors from "@styling/colors";
import { spacing } from "@styling/sizing";
import type { StoreId } from "@KassaroAB/shared/types/ids";
import type { RestApiClient } from "@KassaroAB/shared/types/apiKey";
import { ApiScope } from "@KassaroAB/shared/types/enums/ApiScope";
import {
  useCreateApiClientMutation,
  useDeleteApiClientMutation,
  useGetApiClientsQuery,
  useRevokeApiClientMutation,
} from "../../query/apiClientsQuery";
import { formatDateTime } from "../../utils/dateUtils";
import {
  SettingsActions as Actions,
  SettingsChoice as Choice,
  SettingsField as Field,
  SettingsFieldset as Fieldset,
  SettingsMuted as Muted,
  SettingsSection as Section,
  StatusBadge as Badge,
} from "./SettingsSection";

const ScopeText = styled.span`
  display: flex;
  flex-direction: column;
`;

const ClientRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  justify-content: space-between;
  gap: ${spacing.x2}px;
  padding: ${spacing.x2}px 0;
  border-top: 1px solid ${colors.border};
`;

const ClientInfo = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${spacing.x05}px;
`;

const ClientName = styled.span`
  font-weight: 600;
`;

export const ApiClientsSettingsView = () => {
  const { t } = useTranslation();
  const storeId = useParams<{ storeId: StoreId }>().storeId ?? null;
  const { data: clients, isLoading } = useGetApiClientsQuery(storeId);
  const create = useCreateApiClientMutation(storeId);
  const revoke = useRevokeApiClientMutation(storeId);
  const remove = useDeleteApiClientMutation(storeId);
  const [name, setName] = useState("");
  const [scopes, setScopes] = useState<ApiScope[]>([ApiScope.PRODUCTS_READ]);
  const [createdKey, setCreatedKey] = useState<string | null>(null);
  const [pendingDelete, setPendingDelete] = useState<RestApiClient | null>(
    null,
  );
  const [pendingRevoke, setPendingRevoke] = useState<RestApiClient | null>(
    null,
  );

  const toggleScope = (scope: ApiScope) =>
    setScopes((current) =>
      current.includes(scope)
        ? current.filter((value) => value !== scope)
        : [...current, scope],
    );

  const submit = (event: React.FormEvent) => {
    event.preventDefault();
    create.mutate(
      { name: name.trim(), scopes },
      {
        onSuccess: (result) => {
          setCreatedKey(result.plainKey);
          setName("");
        },
      },
    );
  };

  const confirmDelete = () => {
    if (pendingDelete) {
      remove.mutate(pendingDelete.id);
    }
    setPendingDelete(null);
  };

  const confirmRevoke = () => {
    if (pendingRevoke) {
      revoke.mutate(pendingRevoke.id);
    }
    setPendingRevoke(null);
  };

  const renderClients = () => {
    if (isLoading) {
      return <LoadingContainer message={t("storeSettings.loading")} />;
    }
    if (!clients || clients.length === 0) {
      return <Muted>{t("storeSettings.apiClients.empty")}</Muted>;
    }
    return (
      <div>
        {clients.map((client) => {
          const active = client.isActive !== false;
          return (
            <ClientRow key={client.id}>
              <ClientInfo>
                <span>
                  <ClientName>{client.name}</ClientName>
                  <Badge $tone={active ? "good" : "muted"}>
                    {active
                      ? t("storeSettings.apiClients.active")
                      : t("storeSettings.apiClients.revoked")}
                  </Badge>
                </span>
                <Muted>
                  {t("storeSettings.apiClients.keyEnding", {
                    last4: client.keyLast4 ?? "····",
                  })}
                  {" · "}
                  {(client.scopes ?? [])
                    .map((scope) =>
                      t(`storeSettings.apiClients.scope.${scope}`),
                    )
                    .join(", ")}
                </Muted>
                <Muted>
                  {t("storeSettings.apiClients.created", {
                    date: formatDateTime(client.createdAt),
                  })}
                  {" · "}
                  {client.lastUsedAt
                    ? t("storeSettings.apiClients.lastUsed", {
                        date: formatDateTime(client.lastUsedAt),
                      })
                    : t("storeSettings.apiClients.neverUsed")}
                </Muted>
              </ClientInfo>
              <Actions>
                {active ? (
                  <Button
                    type="button"
                    $small
                    $variant="outlined"
                    $color="warning"
                    disabled={revoke.isPending}
                    onClick={() => setPendingRevoke(client)}
                  >
                    {t("storeSettings.apiClients.revoke")}
                  </Button>
                ) : null}
                <Button
                  type="button"
                  $small
                  $variant="outlined"
                  $color="danger"
                  onClick={() => setPendingDelete(client)}
                >
                  {t("common.delete")}
                </Button>
              </Actions>
            </ClientRow>
          );
        })}
      </div>
    );
  };

  return (
    <>
      <Section as="section" aria-labelledby="api-clients-title">
        <div>
          <SectionTitle id="api-clients-title">
            {t("storeSettings.apiClients.title")}
          </SectionTitle>
          <Muted>{t("storeSettings.apiClients.description")}</Muted>
        </div>
        <form onSubmit={submit}>
          <Section as="div">
            <Field>
              {t("storeSettings.apiClients.name")}
              <StyledInput
                value={name}
                maxLength={100}
                placeholder={t("storeSettings.apiClients.namePlaceholder")}
                onChange={(event) => setName(event.target.value)}
                required
              />
            </Field>
            <Fieldset>
              <legend>{t("storeSettings.apiClients.access")}</legend>
              {Object.values(ApiScope).map((scope) => (
                <Choice key={scope}>
                  <input
                    type="checkbox"
                    checked={scopes.includes(scope)}
                    onChange={() => toggleScope(scope)}
                  />
                  <ScopeText>
                    <span>
                      {t(`storeSettings.apiClients.scope.${scope}`)}{" "}
                      <code>{scope}</code>
                    </span>
                    <Muted as="span">
                      {t(`storeSettings.apiClients.scopeHint.${scope}`)}
                    </Muted>
                  </ScopeText>
                </Choice>
              ))}
            </Fieldset>
            {create.error ? (
              <InfoBox variant="error">{create.error.message}</InfoBox>
            ) : null}
            <Actions>
              <Button
                type="submit"
                disabled={
                  create.isPending || !name.trim() || scopes.length === 0
                }
              >
                {t("storeSettings.apiClients.create")}
              </Button>
            </Actions>
          </Section>
        </form>
        {createdKey ? (
          <OneTimeSecret
            value={createdKey}
            message={t("storeSettings.apiClients.showOnce")}
            onDone={() => setCreatedKey(null)}
          />
        ) : null}
        {renderClients()}
      </Section>
      <ConfirmDialog
        isOpen={pendingDelete !== null}
        title={t("storeSettings.apiClients.deleteTitle")}
        message={t("storeSettings.apiClients.deleteMessage", {
          name: pendingDelete?.name ?? "",
        })}
        confirmLabel={t("common.delete")}
        cancelLabel={t("common.cancel")}
        danger
        onConfirm={confirmDelete}
        onCancel={() => setPendingDelete(null)}
      />
      <ConfirmDialog
        isOpen={pendingRevoke !== null}
        title={t("storeSettings.apiClients.revokeTitle")}
        message={t("storeSettings.apiClients.revokeMessage", {
          name: pendingRevoke?.name ?? "",
        })}
        confirmLabel={t("storeSettings.apiClients.revoke")}
        cancelLabel={t("common.cancel")}
        danger
        onConfirm={confirmRevoke}
        onCancel={() => setPendingRevoke(null)}
      />
    </>
  );
};
